import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, Eyebrow } from "./Reveal";

const IMG = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80`;

const ARTICLES = [
  {
    id: "bureau-minimaliste", tag: "Bureau", time: "5 min", img: IMG("1558478551-1a378f63328e"),
    title: "Structurer un espace de travail minimaliste",
    body: [
      "Un bureau efficace commence par ce que l'on retire. Gardez sur la surface uniquement ce que vous touchez chaque jour : écran, clavier, carnet, une source de lumière chaude.",
      "Surélevez votre écran à hauteur des yeux : votre nuque vous remerciera dès la première semaine. Un support en aluminium suffit, inutile d'investir dans un bureau neuf.",
      "Enfin, créez une frontière : une bougie allumée en début de journée, éteinte à la fin. Ce petit rituel signale au cerveau que le travail commence — et qu'il s'arrête.",
    ],
  },
  {
    id: "rituel-matin", tag: "Bien-être", time: "4 min", img: IMG("1648587385042-b5e2d3965bc0"),
    title: "Le rituel du matin pour une journée sereine",
    body: [
      "Avant d'ouvrir votre messagerie, accordez-vous dix minutes. Une tasse, une fenêtre, un carnet ouvert.",
      "Écrivez trois lignes : ce qui compte aujourd'hui, ce qui peut attendre, et une chose pour laquelle vous êtes reconnaissant. Ce n'est pas de la productivité, c'est de la clarté.",
      "Associez ce moment à une odeur — agrumes le matin, lavande le soir. L'ancrage olfactif rend le rituel automatique en moins de trois semaines.",
    ],
  },
  {
    id: "business-notion", tag: "Business", time: "6 min", img: IMG("1708765275942-091b2e04905c"),
    title: "Piloter son activité en ligne avec un seul tableau",
    body: [
      "La dispersion est l'ennemie des indépendants. Un seul espace pour vos projets, vos finances et vos contenus, et vous gagnez des heures chaque semaine.",
      "Commencez par trois vues : les tâches de la semaine, le calendrier éditorial, et un suivi simple de chiffre d'affaires. Tout le reste est optionnel.",
      "Notion propose des modèles gratuits qui reprennent cette structure, prêts à dupliquer : vous commencez à travailler au lieu de configurer.",
    ],
  },
];

export const Journal = () => {
  const [open, setOpen] = useState(null);
  return (
    <section id="journal" data-testid="journal-section" className="mx-auto max-w-[1440px] px-5 py-24 sm:px-10 sm:py-32">
      <Reveal className="mb-14">
        <Eyebrow>(04) — Le Journal</Eyebrow>
        <h2 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
          Lire, <em className="italic text-[var(--terra)]">ralentir</em>, mieux faire.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
        {ARTICLES.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.1} className={i === 1 ? "md:mt-20" : ""}>
            <button data-testid={`journal-article-${a.id}`} onClick={() => setOpen(a)} className="group block w-full text-left">
              <div className="aspect-[4/5] overflow-hidden rounded-[20px]">
                <img src={a.img} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
              </div>
              <p className="font-mono-label mt-5 text-[11px] text-[var(--muted)]">{a.tag} · {a.time}</p>
              <h3 className="font-display mt-2 text-2xl leading-snug group-hover:text-[var(--terra-deep)] transition-colors">{a.title}</h3>
            </button>
          </Reveal>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="journal-article-modal" className="max-h-[88vh] max-w-2xl overflow-y-auto bg-[var(--bone)]" data-lenis-prevent>
          {open && (
            <>
              <img src={open.img} alt="" className="aspect-[16/9] w-full rounded-xl object-cover" />
              <p className="font-mono-label text-[11px] text-[var(--muted)]">{open.tag} · {open.time}</p>
              <DialogTitle className="font-display text-3xl font-light leading-tight">{open.title}</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-4 text-base leading-relaxed text-[var(--muted)]">
                  {open.body.map((b, i) => <p key={i}>{b}</p>)}
                </div>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
